import React, { useEffect, useState } from 'react';
import './App.css';
import Hero from './components/Hero';
import About from './components/About';
import Car from './components/Car';
import FAQ from './components/FAQ';
import Contact from './components/Contact';
import Schedule from './components/Schedule';
import AppointmentSearch from './components/AppointmentSearch';
import ProcessPage from './components/ProcessPage';
import ReceiptPage from './components/ReceiptPage';
import ReceiptHistory from './components/ReceiptHistory';
import ReceiptViewer from './components/ReceiptViewer';
import BottomNav from './components/BottomNav';
import { useStableScrollAnimation } from './hooks/useStableScrollAnimation';

function App() {
  const [scrollProgress, setScrollProgress] = useState(0);
  const [currentSection, setCurrentSection] = useState('hero');
  const [currentPage, setCurrentPage] = useState('home');
  const { scrollDirection } = useStableScrollAnimation();

  // Handle URL routing
  useEffect(() => {
    const handleRouteChange = () => {
      const path = window.location.pathname;
      if (path === '/process') {
        setCurrentPage('process');
      } else if (path === '/receipt') {
        setCurrentPage('receipt');
      } else if (path === '/receipt-history') {
        setCurrentPage('receipt-history');
      } else if (path.startsWith('/v/')) {
        setCurrentPage('receipt-viewer');
      } else {
        setCurrentPage('home');
        // Reset scroll progress when returning to home page
        setScrollProgress(0);
        setCurrentSection('hero');
        // Scroll to top of page
        window.scrollTo(0, 0);
      }
    };

    // Listen for URL changes
    window.addEventListener('popstate', handleRouteChange);
    handleRouteChange(); // Check initial URL

    return () => window.removeEventListener('popstate', handleRouteChange);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      if (currentPage !== 'home') return; // Only handle scroll on home page
      
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
      setScrollProgress(scrollPercent);

      // Determine current section based on scroll position
      const sections = ['hero', 'about', 'car', 'faq', 'contact', 'schedule', 'appointment-search'];
      const sectionHeight = window.innerHeight;
      const currentSectionIndex = Math.floor(scrollTop / sectionHeight);
      const newCurrentSection = sections[Math.min(currentSectionIndex, sections.length - 1)] || 'hero';
      
      if (newCurrentSection !== currentSection) {
        setCurrentSection(newCurrentSection);
      }
    };

    // Enhanced anchor scrolling handler
    const handleAnchorScroll = (event) => {
      const target = event.target.closest('a[href*="#schedule"]');
      if (target) {
        event.preventDefault();
        const element = document.getElementById('schedule');
        if (element) {
          const elementRect = element.getBoundingClientRect();
          const elementTop = elementRect.top + window.pageYOffset;
          const offset = 80; // Account for fixed headers
          
          window.scrollTo({
            top: elementTop - offset,
            behavior: 'smooth'
          });
          
          // Update URL hash
          window.history.pushState(null, null, '#schedule');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    document.addEventListener('click', handleAnchorScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('click', handleAnchorScroll);
    };
  }, [currentSection]);

  const getSectionClass = (sectionName) => {
    return 'scroll-section loaded'; // Always loaded now, animations handled by global system
  };

  const getBackgroundClass = () => {
    const directionClass = scrollDirection === 'up' ? 'scroll-up' : 'scroll-down';
    return `scroll-bg bg-unified ${directionClass}`;
  };

  // Render Process Page
  if (currentPage === 'process') {
    return (
      <div className="App">
        <ProcessPage />
      </div>
    );
  }

  // Render Receipt Page
  if (currentPage === 'receipt') {
    return (
      <div className="App">
        <ReceiptPage />
      </div>
    );
  }

  // Render Receipt History Page
  if (currentPage === 'receipt-history') {
    return (
      <div className="App">
        <ReceiptHistory />
      </div>
    );
  }

  // Render Receipt Viewer Page
  if (currentPage === 'receipt-viewer') {
    return (
      <div className="App">
        <ReceiptViewer />
      </div>
    );
  }

  // Render Home Page
  return (
    <div className="App">
      {/* Scroll Progress Indicator */}
      <div 
        className={`scroll-progress-section ${scrollDirection === 'up' ? 'scroll-up' : ''}`}
        style={{ width: `${scrollProgress}%` }}
      ></div>
      
      {/* Dynamic Background */}
      <div className={getBackgroundClass()}></div>
      
      
      {/* Hero Section */}
      <div className={getSectionClass('hero')}>
        <Hero />
      </div>
      
      {/* About Section */}
      <div className={getSectionClass('about')}>
        <About />
      </div>
      
      {/* Car Section */}
      <div className={getSectionClass('car')}>
        <Car />
      </div>
      
      {/* FAQ Section */}
      <div className={getSectionClass('faq')}>
        <FAQ />
      </div>
      
      {/* Contact Section */}
      <div className={getSectionClass('contact')}>
        <Contact />
      </div>
      
      {/* Schedule Section */}
      <div className={getSectionClass('schedule')}>
        <Schedule />
      </div>
      
      {/* Appointment Search Section */}
      <div className={getSectionClass('appointment-search')}>
        <AppointmentSearch />
      </div>
      
      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}

export default App;
